import type { SharedReconciliationTaskView, SharedSignatureTaskView } from "@closer/shared";
import { CONTRACT_SIGNER_STATUS_NAMES } from "@closer/shared";
import { Badge, Button, Card, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  ReconciliationDecisionDialog,
  type ReconciliationDecisionTarget,
} from "../../components/ReconciliationDecisionDialog";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * PAYLAŞILAN YÜZEYİN BEKLEYEN İŞLERİ (CLOS-883).
 *
 * ### ⚠ NEDEN VAR: KOMUT VARDI, ORAYA GİDEN YOL YOKTU
 *
 * Karşı taraf mutabakat kararını ve imzasını `/api/shared-contracts` üzerinden verebiliyordu
 * — ama hangi görevin ya da hangi imzacı satırının beklediğini hiçbir ekrandan öğrenemiyordu.
 * Okuma ucu yoktu, bekleyen-işlem projektörü bu iki olayı işlemiyordu ve paylaşılabilir
 * nesne türleri (`version|clause|work_item|file`) ikisini de içermiyordu. Yani komutlar
 * fiilen erişilemezdi: "kullanıcı oraya içeriden nasıl geliyor?" sorusunun cevabı yoktu.
 *
 * ### ⚠ BÖLÜM YOKSA ÇİZİLMİYOR — boş kutu DEĞİL
 *
 * Bekleyen iş yoksa bölüm hiç görünmüyor. Boş bir kutu "bekleyen işiniz yok" der ve bu
 * cümle doğru olsa bile ekranı, karşı tarafın hiçbir zaman göremeyeceği iki başlıkla
 * doldururdu: mutabakat ve imza akışları her paylaşımda olmuyor.
 *
 * ### ⚠ HATA DALI OKUNUYOR — AMA 403 HATA DEĞİL
 *
 * Sorgu düşerse bölüm "bekleyen iş yok" demiyor: sessizce boş görünmek, karşı tarafın
 * kaçırdığı bir imzayı fark etmemesi demek olurdu (bu depoda "kapıyı besleyen sorgu
 * düşerse kapı KAPANMALI" kuralı).
 *
 * ⚠ Tek istisna 403 ve gerekçesi `enabled: yetkili` ile AYNI: "bu bölüm sana ait değil"
 * bir arıza değil. Hizmet sağlayıcı bunu canlıda gösterdi (CLOS-1185, UAT-B): kendi
 * kiracısında `contract.reconcile` rolü olduğu için sorgu açılıyor, ama sağlayıcı yolunun
 * workflow çarpanı imza/mutabakat içermediği için uç haklı olarak 403 veriyor — ekranda
 * ise "Kayıt bulunamadı" hata kutusu çiziliyordu. Kendi rolüne bakmak yetmiyor: efektif
 * yetki kesişimde belirleniyor ve onu yalnız sunucu biliyor.
 */

function bolumDurumu(
  sorgu: { isPending: boolean; error: unknown; refetch: () => unknown },
  baslik: string,
) {
  if (sorgu.isPending) return <Skeleton lines={2} />;
  if (sorgu.error) {
    /**
     * ⚠ "Yetkin yok" ≠ "arıza": `null` dönüyor, çağıran da veri boş olduğu için bölümü
     * hiç çizmiyor. Ayrı bir işaret değeri uydurmadık — "gösterilecek durum yok" cümlesi
     * bu fonksiyonda zaten `null`.
     */
    if (sorgu.error instanceof ApiRequestError && sorgu.error.status === 403) return null;
    return (
      <StateView
        state={resolveErrorState(sorgu.error, {
          idempotent: true,
          overrides: {
            error: { title: baslik, description: "Bölüm şu an getirilemedi." },
          },
        })}
        onRetry={() => void sorgu.refetch()}
      />
    );
  }
  return null;
}

export function SharedReconciliationTasksSection({ contractId }: { contractId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const { preferences } = usePreferences();
  const [hedef, setHedef] = useState<ReconciliationDecisionTarget | null>(null);

  /**
   * ⚠ Sorgu KENDİ ROL KAPISINA bağlı: uç `contract.reconcile` istiyor ve yetkisiz kullanıcı
   * için 403 dönüyor. `enabled` olmasaydı ekran her açılışta bir hata kutusu çizerdi —
   * oysa yetkisi olmayan için ortada bir arıza yok, sadece bu bölüm ona ait değil.
   */
  const yetkili = can("contract.reconcile");
  const anahtar = tenantKey(tenantId, "ortak-sozlesme", contractId, "mutabakat-gorevleri");
  const gorevler = useQuery({
    queryKey: anahtar,
    enabled: yetkili,
    queryFn: async () =>
      (
        await api<SharedReconciliationTaskView[]>(
          `/api/shared-contracts/${contractId}/reconciliation-tasks`,
          { tenantId },
        )
      ).data,
  });

  if (!yetkili) return null;

  const durum = bolumDurumu(gorevler, "Bekleyen mutabakat görevleri yüklenemedi");
  /** ⚠ Yalnız YÜKLENMİŞ ve BOŞ olduğunda gizleniyor: hata dalı yukarıda çiziliyor. */
  if (!durum && (gorevler.data ?? []).length === 0) return null;

  return (
    <Card>
      <h2 className="mb-3 text-base font-semibold text-fg-heading">Sizden beklenen mutabakat</h2>
      {durum ?? (
        <ul className="flex flex-col gap-2">
          {(gorevler.data ?? []).map((gorev) => (
            <li
              key={gorev.taskId}
              className="flex flex-wrap items-center gap-3 rounded-md border border-border-default p-3"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-sm font-medium text-fg-heading">
                  Sürüm {gorev.versionNumber}
                </span>
                <span className="text-xs text-fg-soft">
                  {gorev.description ?? "Açıklama girilmemiş"}
                  {/* ⚠ `null` "tarih yok" demek, "süresi geçti" değil. */}
                  {gorev.dueDate
                    ? ` · Son tarih ${formatDate(gorev.dueDate, preferences.locale)}`
                    : ""}
                </span>
              </div>
              <Button
                size="sm"
                onClick={() =>
                  setHedef({ reconciliationId: gorev.reconciliationId, taskId: gorev.taskId })
                }
              >
                Karar ver
              </Button>
            </li>
          ))}
        </ul>
      )}

      <ReconciliationDecisionDialog
        open={hedef !== null}
        hedef={hedef}
        kararYolu={(h) =>
          `/api/shared-contracts/${contractId}/reconciliations/${h.reconciliationId}/tasks/${h.taskId}/decide`
        }
        onClose={() => setHedef(null)}
        onDone={async () => {
          setHedef(null);
          await queryClient.invalidateQueries({ queryKey: anahtar });
        }}
      />
    </Card>
  );
}

export function SharedSignatureTasksSection({ contractId }: { contractId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const yetkili = can("contract.sign");
  const anahtar = tenantKey(tenantId, "ortak-sozlesme", contractId, "imza-gorevleri");
  const satirlar = useQuery({
    queryKey: anahtar,
    enabled: yetkili,
    queryFn: async () =>
      (
        await api<SharedSignatureTaskView[]>(
          `/api/shared-contracts/${contractId}/signature-tasks`,
          { tenantId },
        )
      ).data,
  });

  const komut = useMutation({
    mutationFn: (girdi: { requestId: string; signerId: string; ad: "sign" | "decline" }) =>
      api(
        `/api/shared-contracts/${contractId}/signature-requests/${girdi.requestId}/signers/${girdi.signerId}/${girdi.ad}`,
        { method: "POST", tenantId, body: JSON.stringify({}) },
      ),
    onSuccess: async (_veri, girdi) => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success(girdi.ad === "sign" ? "İmzanız kaydedildi" : "İmza reddiniz kaydedildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İşlem tamamlanamadı"),
  });

  if (!yetkili) return null;

  const durum = bolumDurumu(satirlar, "Bekleyen imzalar yüklenemedi");
  if (!durum && (satirlar.data ?? []).length === 0) return null;

  return (
    <Card>
      <h2 className="mb-3 text-base font-semibold text-fg-heading">Sizden beklenen imza</h2>
      {durum ?? (
        <ul className="flex flex-col gap-2">
          {(satirlar.data ?? []).map((satir) => (
            <li
              key={satir.signerId}
              className="flex flex-wrap items-center gap-3 rounded-md border border-border-default p-3"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-sm font-medium text-fg-heading">
                  Sürüm {satir.versionNumber}
                </span>
                <span className="text-xs text-fg-soft">
                  {satir.required ? "Zorunlu imzacı" : "Opsiyonel imzacı"}
                  {satir.signingMode === "serial" ? ` · Sıra ${satir.position}` : ""}
                </span>
              </div>
              {/* ⚠ Kendi satırının durumu — talebin durumu DEĞİL (bkz. tip notu). */}
              <Badge tone="neutral">{CONTRACT_SIGNER_STATUS_NAMES[satir.status]}</Badge>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  loading={komut.isPending}
                  onClick={() =>
                    komut.mutate({
                      requestId: satir.requestId,
                      signerId: satir.signerId,
                      ad: "sign",
                    })
                  }
                >
                  İmzala
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={komut.isPending}
                  onClick={() =>
                    komut.mutate({
                      requestId: satir.requestId,
                      signerId: satir.signerId,
                      ad: "decline",
                    })
                  }
                >
                  Reddet
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
